import React, {
  memo,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ITransactionForCashReportResponse } from '../../apis/models/transactions';
import {
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
  differenceInCalendarDays,
  format,
} from 'date-fns';
import { enZA, fr } from 'date-fns/locale';
import MainCard from '../../components/MainCard';
import Grid from '@mui/material/Grid/Grid';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import IncomesCashReportDetails from './incomes-cash-report-details';
import ExpenseCashReportDetails from './expense-cash-report-details';
import {
  calculateTotalAndPercentageForExpenses,
  calculateTotalAndPercentageForIncomes,
  GroupedCashReportData,
} from './stats-report';
import {
  useLazyGetReportSummaryForPeriodQuery,
  useLazyGetComparePeriodsQuery,
  useGetAllSchoolsQuery,
  useGetLoggedInUserQuery,
  useGetClientFeeTypeQuery,
} from '../../apis/konektApi';
import { getIncomesFeeTypes, getExpensesFeeTypes } from '../../store/reducers/feeTypesReducer';
import DatePresetBar from './DatePresetBar';
import ReportSummaryCards from './ReportSummaryCards';
import ReportDownloadButton from './ReportDownloadButton';
import PeriodComparisonSummaryCards from './PeriodComparisonSummaryCards';
import PeriodComparisonTable from './PeriodComparisonTable';
import PeriodComparisonChart from './PeriodComparisonChart';

const DEFAULT_PERIOD_B_SELECTION = 'default';

function getDefaultPeriodB(start: Date, end: Date): { start: Date; end: Date } {
  const lengthDays = differenceInCalendarDays(end, start) + 1;
  const periodBEnd = endOfDay(subDays(start, 1));
  const periodBStart = startOfDay(subDays(periodBEnd, lengthDays - 1));
  return { start: periodBStart, end: periodBEnd };
}

// selection is either DEFAULT_PERIOD_B_SELECTION or a 'yyyy-MM' month key from the picker below.
function computePeriodBForSelection(
  selection: string,
  periodAStart: Date,
  periodAEnd: Date
): { start: Date; end: Date } {
  if (selection === DEFAULT_PERIOD_B_SELECTION) {
    return getDefaultPeriodB(periodAStart, periodAEnd);
  }
  const [year, month] = selection.split('-').map(Number);
  const monthDate = new Date(year, month - 1, 1);
  return { start: startOfMonth(monthDate), end: endOfMonth(monthDate) };
}

function formatRangeLabel(start: Date, end: Date): string {
  return `${format(start, 'dd MMM yyyy')} — ${format(end, 'dd MMM yyyy')}`;
}

interface ICashReportDetailsProps {
  getCashReportDetails: (dateFrom: string, dateEnd: string) => void;
  cashReportSummary?: ITransactionForCashReportResponse;
}

function getTabComponentByIndex(index: number) {
  return {
    id: `component-tab-${index}`,
    'aria-controls': `component-tabpanel-${index}`,
  };
}

const TabPanel = ({
  children,
  value,
  index,
}: {
  children?: ReactElement | ReactNode | string;
  value: number;
  index: number;
}) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  );
};

export const CashReportPage: React.FC<ICashReportDetailsProps> = memo(
  ({ getCashReportDetails, cashReportSummary }) => {
    const { t } = useTranslation();

    const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
      start: startOfDay(subDays(new Date(), 1)),
      end: endOfDay(subDays(new Date(), 1)),
    });
    const [customStart, setCustomStart] = useState<Date | null>(null);
    const [customEnd, setCustomEnd] = useState<Date | null>(null);

    const [triggerBalanceReport, { data: balanceData, isLoading: balanceLoading }] =
      useLazyGetReportSummaryForPeriodQuery();

    const [periodBSelection, setPeriodBSelection] = useState<string>(DEFAULT_PERIOD_B_SELECTION);
    const [periodB, setPeriodB] = useState<{ start: Date; end: Date } | null>(null);
    const [
      triggerComparePeriods,
      { data: comparisonData, isLoading: comparisonLoading, error: comparisonError },
    ] = useLazyGetComparePeriodsQuery();

    const { data: schools = [] } = useGetAllSchoolsQuery();
    const { data: loggedInUser } = useGetLoggedInUserQuery();

    // Always fetch fee types on mount — RTK Query deduplicates within the session.
    // The reducer also restores from localStorage so translations are instantly available.
    const { isFetching: feeTypesFetching } = useGetClientFeeTypeQuery(undefined);
    const incomeFeeTypes = useSelector(getIncomesFeeTypes);
    const expenseFeeTypes = useSelector(getExpensesFeeTypes);

    const { i18n } = useTranslation();
    const isFr = i18n.language.includes('fr');

    const resolveFeeType = useCallback(
      (enumKey: string): string => {
        const found = [...incomeFeeTypes, ...expenseFeeTypes].find((ft) => ft.enum_key === enumKey);
        if (found) return isFr ? found.value_fr : found.value_en;
        return enumKey;
      },
      [incomeFeeTypes, expenseFeeTypes, isFr]
    );

    const [groupedIncomesStats, setGroupedIncomeStats] = useState<GroupedCashReportData[]>([]);
    const [groupedExpensesStats, setGroupedExpensesStats] = useState<GroupedCashReportData[]>([]);
    const [currentTab, setCurrentTab] = React.useState(0);

    const handleTabChange = (_: SyntheticEvent, newValue: number) => {
      setCurrentTab(newValue);
    };

    const lookupForPeriod = (start?: Date, end?: Date) => {
      const s = start || dateRange.start;
      const e = end || dateRange.end;
      getCashReportDetails(s.toISOString(), e.toISOString());
      triggerBalanceReport({ startDate: s.toISOString(), endDate: e.toISOString() });
    };

    const fetchComparison = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) => {
      triggerComparePeriods({
        periodAStart: aStart.toISOString(),
        periodAEnd: aEnd.toISOString(),
        periodBStart: bStart.toISOString(),
        periodBEnd: bEnd.toISOString(),
      });
    };

    const handlePresetChange = (start: Date, end: Date) => {
      setDateRange({ start, end });
      lookupForPeriod(start, end);
      // Selection 'default' tracks period A automatically; a pinned month stays put.
      const nextPeriodB = computePeriodBForSelection(periodBSelection, start, end);
      setPeriodB(nextPeriodB);
      fetchComparison(start, end, nextPeriodB.start, nextPeriodB.end);
    };

    const handlePeriodBSelectionChange = (event: SelectChangeEvent<string>) => {
      const selection = event.target.value;
      setPeriodBSelection(selection);
      const nextPeriodB = computePeriodBForSelection(selection, dateRange.start, dateRange.end);
      setPeriodB(nextPeriodB);
      fetchComparison(dateRange.start, dateRange.end, nextPeriodB.start, nextPeriodB.end);
    };

    // Last 24 months for the "compare to a specific month" picker, newest first.
    const monthOptions = React.useMemo(() => {
      const locale = isFr ? fr : enZA;
      return Array.from({ length: 24 }, (_, i) => {
        const monthDate = subMonths(new Date(), i);
        return {
          value: format(monthDate, 'yyyy-MM'),
          label: format(monthDate, 'MMMM yyyy', { locale }),
        };
      });
    }, [isFr]);

    useEffect(() => {
      lookupForPeriod();
      const initialPeriodB = getDefaultPeriodB(dateRange.start, dateRange.end);
      setPeriodB(initialPeriodB);
      fetchComparison(dateRange.start, dateRange.end, initialPeriodB.start, initialPeriodB.end);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (cashReportSummary) {
        setGroupedIncomeStats(calculateTotalAndPercentageForIncomes(cashReportSummary.incomes));
        setGroupedExpensesStats(calculateTotalAndPercentageForExpenses(cashReportSummary.expenses));
      }
    }, [cashReportSummary]);

    // Rows include a synthetic "TOTALS" entry whose periodTotal already sums every other
    // row — reducing over all rows would double-count it, so it's excluded here.
    const totalIncome =
      balanceData?.incomes
        .filter((item) => item.feeType !== 'TOTALS')
        .reduce((sum, item) => sum + item.periodTotal, 0) ?? 0;
    const totalExpenses =
      balanceData?.expenses
        .filter((item) => item.feeType !== 'TOTALS')
        .reduce((sum, item) => sum + item.periodTotal, 0) ?? 0;

    const schoolName = schools?.[0] ? (schools[0] as any).name || 'School' : 'School';
    const userName = loggedInUser
      ? `${(loggedInUser as any).firstName || ''} ${(loggedInUser as any).lastName || ''}`.trim()
      : '';

    const labelA = formatRangeLabel(dateRange.start, dateRange.end);
    const labelB = periodB ? formatRangeLabel(periodB.start, periodB.end) : '';

    return (
      <MainCard title={t('titles.balance_details')}>
        <Box>
          <Grid item xs={12} md={7} lg={8}>
            <DatePresetBar
              onChange={handlePresetChange}
              startDate={customStart}
              endDate={customEnd}
              onCustomChange={(s, e) => {
                setCustomStart(s);
                setCustomEnd(e);
              }}
            />

            <ReportSummaryCards
              totalIncome={totalIncome}
              totalExpenses={totalExpenses}
              currency="$"
              isLoading={balanceLoading}
            />

            <Box mt={2}>
              {cashReportSummary && (
                <Grid container rowSpacing={1} columnSpacing={1}>
                  <Grid item xs={12}>
                    <Box>
                      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                          value={currentTab}
                          onChange={handleTabChange}
                          aria-label="cash report tabs"
                          sx={{ px: 3, pt: 1, '& .MuiTab-root': { mb: 0.5 } }}
                        >
                          <Tab
                            label={t('reports_breakdown.incomes')}
                            {...getTabComponentByIndex(0)}
                          />
                          <Tab
                            label={t('reports_breakdown.expenses')}
                            {...getTabComponentByIndex(1)}
                          />
                          <Tab
                            label={t('reports_breakdown.balance')}
                            {...getTabComponentByIndex(2)}
                          />
                        </Tabs>
                      </Box>
                      <Box sx={{ p: 3 }}>
                        <TabPanel value={currentTab} index={0}>
                          <Box display="flex" justifyContent="flex-end" mb={2}>
                            <ReportDownloadButton
                              cashReport={cashReportSummary}
                              summaryData={balanceData}
                              dateRange={dateRange}
                              schoolName={schoolName}
                              userName={userName}
                              resolveFeeType={resolveFeeType}
                              currentTab={0}
                              disabled={!cashReportSummary || feeTypesFetching}
                            />
                          </Box>
                          <IncomesCashReportDetails
                            incomesTransactionResult={cashReportSummary.incomes}
                            expenseTransactionResult={cashReportSummary.expenses}
                            groupedIncomesStats={groupedIncomesStats}
                          />
                        </TabPanel>
                        <TabPanel value={currentTab} index={1}>
                          <Box display="flex" justifyContent="flex-end" mb={2}>
                            <ReportDownloadButton
                              cashReport={cashReportSummary}
                              summaryData={balanceData}
                              dateRange={dateRange}
                              schoolName={schoolName}
                              userName={userName}
                              resolveFeeType={resolveFeeType}
                              currentTab={1}
                              disabled={!cashReportSummary || feeTypesFetching}
                            />
                          </Box>
                          <ExpenseCashReportDetails
                            expenseTransactionResult={cashReportSummary.expenses}
                            incomesTransactionResult={cashReportSummary.incomes}
                            groupedExpensesStats={groupedExpensesStats}
                          />
                        </TabPanel>
                        <TabPanel value={currentTab} index={2}>
                          <Box display="flex" justifyContent="flex-end" mb={2}>
                            <ReportDownloadButton
                              cashReport={cashReportSummary}
                              summaryData={balanceData}
                              dateRange={dateRange}
                              schoolName={schoolName}
                              userName={userName}
                              resolveFeeType={resolveFeeType}
                              currentTab={2}
                              disabled={!cashReportSummary || feeTypesFetching || comparisonLoading}
                              comparison={
                                comparisonData ? { data: comparisonData, labelA, labelB } : undefined
                              }
                            />
                          </Box>
                          {periodB && (
                            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
                              <Typography variant="body2" color="text.secondary">
                                {t('report.period_b')}:
                              </Typography>
                              <FormControl size="small" sx={{ minWidth: 220 }}>
                                <Select value={periodBSelection} onChange={handlePeriodBSelectionChange}>
                                  <MenuItem value={DEFAULT_PERIOD_B_SELECTION}>
                                    {t('report.previous_period_default')}
                                  </MenuItem>
                                  {monthOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Typography variant="caption" color="text.secondary">
                                {formatRangeLabel(periodB.start, periodB.end)}
                              </Typography>
                            </Box>
                          )}

                          {comparisonError && !comparisonLoading && (
                            <Typography color="error" variant="body2" mb={2}>
                              {t('errors.generic_error')}
                            </Typography>
                          )}

                          {comparisonData && (
                            <Box mt={2}>
                              <PeriodComparisonSummaryCards
                                totalIncomeA={comparisonData.periodA.totalIncome}
                                totalIncomeB={comparisonData.periodB.totalIncome}
                                totalExpensesA={comparisonData.periodA.totalExpenses}
                                totalExpensesB={comparisonData.periodB.totalExpenses}
                                currency="$"
                                isLoading={comparisonLoading}
                                labelA={labelA}
                                labelB={labelB}
                              />
                              <PeriodComparisonChart
                                periodA={comparisonData.periodA}
                                periodB={comparisonData.periodB}
                                labelA={labelA}
                                labelB={labelB}
                                currency="$"
                              />
                              <PeriodComparisonTable
                                title={t('reports_breakdown.incomes')}
                                periodATotals={comparisonData.periodA.incomes}
                                periodBTotals={comparisonData.periodB.incomes}
                                resolveFeeType={resolveFeeType}
                                currency="$"
                                labelA={labelA}
                                labelB={labelB}
                              />
                              <PeriodComparisonTable
                                title={t('reports_breakdown.expenses')}
                                periodATotals={comparisonData.periodA.expenses}
                                periodBTotals={comparisonData.periodB.expenses}
                                resolveFeeType={resolveFeeType}
                                currency="$"
                                labelA={labelA}
                                labelB={labelB}
                                invertColor
                              />
                            </Box>
                          )}
                        </TabPanel>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>
        </Box>
      </MainCard>
    );
  }
);

export default CashReportPage;
